import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Tenant } from '@types';
import api from '@services/api';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, email: string, password: string, password_confirmation: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  clearError: () => void;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  tenant_name?: string;
  tenant_subdomain?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      isAuthenticated: false,
      isLoading: false,
      isHydrated: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ data: { user: User; tenant: Tenant; access_token: string; refresh_token: string } }>(
            '/auth/login',
            { email, password }
          );


          // Fix: keys are camelCase because of api.ts interceptor
          // @ts-ignore
          const { user, tenant, accessToken, refreshToken } = response.data;
          api.setTokens(accessToken, refreshToken);

          set({
            user,
            tenant,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await api.post('/auth/logout');
        } finally {
          api.clearTokens();
          set({
            user: null,
            tenant: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ data: { user: User; tenant: Tenant; access_token: string; refresh_token: string } }>(
            '/auth/register',
            data
          );


          // Fix: keys are camelCase because of api.ts interceptor
          // @ts-ignore
          const { user, tenant, accessToken, refreshToken } = response.data;
          api.setTokens(accessToken, refreshToken);

          set({
            user,
            tenant,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          await api.post('/auth/forgot-password', { email });
          set({ isLoading: false });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to send reset email',
            isLoading: false,
          });
          throw error;
        }
      },

      resetPassword: async (token: string, email: string, password: string, password_confirmation: string) => {
        set({ isLoading: true, error: null });
        try {
          await api.post('/auth/reset-password', { token, email, password, password_confirmation });
          set({ isLoading: false });
        } catch (error: any) {
          set({
            error: error.message || 'Password reset failed',
            isLoading: false,
          });
          throw error;
        }
      },

      verifyEmail: async (token: string) => {
        set({ isLoading: true, error: null });
        try {
          await api.post('/auth/verify-email', { token });
          set({ isLoading: false });
        } catch (error: any) {
          set({
            error: error.message || 'Email verification failed',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchProfile: async () => {
        try {
          const response = await api.get<{ data: { user: User; tenant: Tenant } }>('/auth/profile');
          set({
            user: response.data.user,
            tenant: response.data.tenant,
            isAuthenticated: true,
          });
        } catch {
          // If the 401 interceptor already cleared tokens, sync Zustand in-memory state.
          // For non-401 errors (500, network), tokens are still valid — don't log out.
          if (!api.isAuthenticated()) {
            set({ user: null, tenant: null, isAuthenticated: false });
          }
        }
      },

      updateProfile: async (data: Partial<User>) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.put<{ data: User }>('/auth/profile', data);
          set({
            user: response.data,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Profile update failed',
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, tenant: state.tenant, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isHydrated = true;
        }
      },
    }
  )
);
